import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Flex } from 'antd';

import DetailSpinner from '@totem/components/common/DetailSpinner';
import { setToken } from '@totem/components/userContext/accountUtilities';
import { MobileLoginProfile } from '@totem/types/user';
import api from '@totem/utilities/api';
import { exchangeAuthTokenForSession } from '@totem/utilities/authSession';
import { isNotNull } from '@totem/utilities/common';
import configuration from '@totem/utilities/configuration';
import logger from '@totem/utilities/logger';
import { peekPostLoginRedirect } from '@totem/utilities/loginRedirect';

const AuthCallback = () => {
  const navigate = useNavigate();
  const {
    error,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
    handleRedirectCallback,
  } = useAuth0();
  const [isExchanging, setIsExchanging] = useState(false);
  const [hasHandledRedirect, setHasHandledRedirect] = useState(false);
  const returnToRef = useRef<string>(null);

  useEffect(() => {
    if (hasHandledRedirect || isLoading) {
      return;
    }

    // TODO: Remove Debug Logging
    if (!configuration.isProd) {
      if (isNotNull(error)) {
        logger.error(`AuthCallback: ERROR = ${error.name}: ${error.message}`);
      }
    }

    const handleRedirect = async () => {
      try {
        const result = await handleRedirectCallback();
        returnToRef.current = result?.appState?.returnTo ?? null;
      } catch {
        navigate('/login', { replace: true });
      } finally {
        setHasHandledRedirect(true);
      }
    };

    handleRedirect();
  }, [handleRedirectCallback, hasHandledRedirect, isLoading, navigate]);

  useEffect(() => {
    // TODO: Remove Debug Logging

    logger.debug(
      `AuthCallback: isAuthenticated = ${isAuthenticated.toString()}`,
    );

    if (isLoading || isExchanging) {
      return;
    }

    if (error) {
      logger.error(`AuthCallback: ERROR = ${error.name}: ${error.message}`);

      navigate('/login', { replace: true });
      return;
    }

    if (!isAuthenticated || !hasHandledRedirect) {
      return;
    }

    const exchangeSession = async () => {
      setIsExchanging(true);
      try {
        const accessToken = await getAccessTokenSilently();
        const idTokenClaims = await getIdTokenClaims();
        const result = await exchangeAuthTokenForSession({
          accessToken,
          idToken: idTokenClaims?.__raw,
        });
        if (!configuration.isProd) {
          if (result.ok) {
            const contentType = result.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
              const payload = (await result.json()) as MobileLoginProfile;
              setToken(payload.accessToken, 'AuthCallback.exchangeSession');
            } else {
              const payload = await result.text();
              logger.debug(
                'AuthCallback: Exchange Session (non-json):',
                payload,
              );
            }
          } else {
            logger.error(
              `Exchange Session: request failed (${result.status} ${result.statusText})`,
            );
          }
        }
        api.setAuthToken();
      } finally {
        const redirectPath = returnToRef.current || peekPostLoginRedirect();
        const hasOrgInPath =
          typeof redirectPath === 'string' &&
          /^\/dashboard\/[a-f0-9]{24}(\/|$)/i.test(redirectPath);

        navigate(hasOrgInPath ? redirectPath : '/switch-organization', {
          replace: true,
        });
      }
    };

    exchangeSession();
  }, [
    error,
    getAccessTokenSilently,
    getIdTokenClaims,
    hasHandledRedirect,
    isAuthenticated,
    isExchanging,
    isLoading,
    navigate,
  ]);

  return (
    <Flex justify={'center'} align={'center'} style={{ height: '100vh' }}>
      <DetailSpinner size={96} />
    </Flex>
  );
};

export default AuthCallback;
